/**
 * Chart Component
 *
 * Wrapper around Chart.js for creating responsive, dark-theme compatible charts.
 * Provides factory functions for common chart types used in reports.
 */

import {
  Chart,
  registerables,
} from 'chart.js';

// Register all Chart.js components
Chart.register(...registerables);

// Dark theme color palette
const CHART_COLORS = {
  primary: 'oklch(0.67 0.18 281)',
  primaryTransparent: 'oklch(0.67 0.18 281 / 0.2)',
  success: 'oklch(0.72 0.17 142)',
  successTransparent: 'oklch(0.72 0.17 142 / 0.2)',
  warning: 'oklch(0.75 0.15 85)',
  warningTransparent: 'oklch(0.75 0.15 85 / 0.2)',
  destructive: 'oklch(0.63 0.24 29)',
  destructiveTransparent: 'oklch(0.63 0.24 29 / 0.2)',
  info: 'oklch(0.70 0.15 230)',
  infoTransparent: 'oklch(0.70 0.15 230 / 0.2)',
  muted: 'oklch(0.55 0 0)',
  border: 'oklch(0.35 0 0)',
  text: 'oklch(0.95 0 0)',
  textMuted: 'oklch(0.65 0 0)',
  background: 'oklch(0.15 0 0)',
};

// Color palette for multi-series charts
const PALETTE = [
  'oklch(0.67 0.18 281)', // primary purple
  'oklch(0.70 0.15 230)', // info blue
  'oklch(0.72 0.17 142)', // success green
  'oklch(0.75 0.15 85)',  // warning yellow
  'oklch(0.63 0.24 29)',  // destructive red
  'oklch(0.70 0.12 320)', // pink
  'oklch(0.65 0.10 180)', // teal
  'oklch(0.55 0 0)',      // neutral
];

// Set Chart.js defaults for dark theme
Chart.defaults.color = CHART_COLORS.textMuted;
Chart.defaults.borderColor = CHART_COLORS.border;
Chart.defaults.font.family = "'Inter', 'system-ui', sans-serif";

/**
 * Creates a container element for the chart with proper styling
 */
function createChartContainer(height: string = '300px'): {
  container: HTMLElement;
  canvas: HTMLCanvasElement;
} {
  const container = document.createElement('div');
  container.className = 'relative w-full';
  container.style.height = height;

  const canvas = document.createElement('canvas');
  container.appendChild(canvas);

  return { container, canvas };
}

export interface ChartInstance {
  element: HTMLElement;
  chart: Chart;
  destroy: () => void;
  update: () => void;
}

/**
 * Creates a doughnut/pie chart for showing category/status distribution
 */
export function createDoughnutChart(
  labels: string[],
  data: number[],
  options?: {
    cutout?: string;
    title?: string;
    formatter?: (value: number) => string;
  }
): ChartInstance {
  const { container, canvas } = createChartContainer('280px');

  const chart = new Chart(canvas, {
    type: 'doughnut',
    data: {
      labels,
      datasets: [
        {
          data,
          backgroundColor: PALETTE.slice(0, data.length),
          borderColor: CHART_COLORS.background,
          borderWidth: 2,
          hoverBorderWidth: 3,
          hoverBorderColor: CHART_COLORS.text,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      cutout: options?.cutout ?? '60%',
      plugins: {
        legend: {
          position: 'right',
          labels: {
            color: CHART_COLORS.textMuted,
            usePointStyle: true,
            padding: 12,
            font: {
              size: 11,
            },
          },
        },
        tooltip: {
          backgroundColor: 'rgba(32, 32, 32, 0.95)',
          titleColor: CHART_COLORS.text,
          bodyColor: CHART_COLORS.textMuted,
          borderColor: CHART_COLORS.border,
          borderWidth: 1,
          cornerRadius: 8,
          padding: 12,
          displayColors: true,
          boxPadding: 4,
          callbacks: {
            label: (context) => {
              const value = context.raw as number;
              const dataArray = context.dataset.data as number[];
              const total = dataArray.reduce((sum, val) => sum + val, 0);
              const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0';
              const formatted = options?.formatter ? options.formatter(value) : value.toLocaleString();
              return `${context.label}: ${formatted} (${percentage}%)`;
            },
          },
        },
      },
    },
  });

  return {
    element: container,
    chart,
    destroy: () => chart.destroy(),
    update: () => chart.update(),
  };
}

/**
 * Creates a line chart for showing trends over time
 */
export function createLineChart(
  labels: string[],
  datasets: Array<{
    label: string;
    data: number[];
    color?: string;
    fill?: boolean;
    dashed?: boolean;
  }>,
  options?: {
    yAxisFormatter?: (value: number) => string;
    tension?: number;
  }
): ChartInstance {
  const { container, canvas } = createChartContainer('300px');

  const chartDatasets = datasets.map((ds, i) => ({
    label: ds.label,
    data: ds.data,
    borderColor: ds.color ?? PALETTE[i % PALETTE.length],
    backgroundColor: ds.fill
      ? `${ds.color ?? PALETTE[i % PALETTE.length]}33`
      : 'transparent',
    fill: ds.fill ?? false,
    tension: options?.tension ?? 0.3,
    pointRadius: 4,
    pointHoverRadius: 6,
    pointBackgroundColor: ds.color ?? PALETTE[i % PALETTE.length],
    pointBorderColor: CHART_COLORS.background,
    pointBorderWidth: 2,
    borderWidth: 2,
    borderDash: ds.dashed ? [5, 5] : [],
  }));

  const chart = new Chart(canvas, {
    type: 'line',
    data: {
      labels,
      datasets: chartDatasets,
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        intersect: false,
        mode: 'index',
      },
      scales: {
        x: {
          grid: {
            color: CHART_COLORS.border,
          },
          ticks: {
            color: CHART_COLORS.textMuted,
            font: {
              size: 11,
            },
          },
          border: {
            color: CHART_COLORS.border,
          },
        },
        y: {
          beginAtZero: true,
          grid: {
            color: CHART_COLORS.border,
          },
          ticks: {
            color: CHART_COLORS.textMuted,
            font: {
              size: 11,
            },
            callback: function(value) {
              if (options?.yAxisFormatter) {
                return options.yAxisFormatter(value as number);
              }
              return value.toLocaleString();
            },
          },
          border: {
            color: CHART_COLORS.border,
          },
        },
      },
      plugins: {
        legend: {
          labels: {
            color: CHART_COLORS.textMuted,
            usePointStyle: true,
            padding: 16,
            font: {
              size: 12,
            },
          },
        },
        tooltip: {
          backgroundColor: 'rgba(32, 32, 32, 0.95)',
          titleColor: CHART_COLORS.text,
          bodyColor: CHART_COLORS.textMuted,
          borderColor: CHART_COLORS.border,
          borderWidth: 1,
          cornerRadius: 8,
          padding: 12,
          displayColors: true,
          boxPadding: 4,
          callbacks: {
            label: (context) => {
              const value = context.raw as number;
              const formatted = options?.yAxisFormatter
                ? options.yAxisFormatter(value)
                : value.toLocaleString();
              return `${context.dataset.label}: ${formatted}`;
            },
          },
        },
      },
    },
  });

  return {
    element: container,
    chart,
    destroy: () => chart.destroy(),
    update: () => chart.update(),
  };
}

/**
 * Creates a bar chart for comparisons
 */
export function createBarChart(
  labels: string[],
  datasets: Array<{
    label: string;
    data: number[];
    color?: string;
  }>,
  options?: {
    horizontal?: boolean;
    stacked?: boolean;
    yAxisFormatter?: (value: number) => string;
  }
): ChartInstance {
  const { container, canvas } = createChartContainer('300px');

  const chartDatasets = datasets.map((ds, i) => ({
    label: ds.label,
    data: ds.data,
    backgroundColor: ds.color ?? PALETTE[i % PALETTE.length],
    borderColor: ds.color ?? PALETTE[i % PALETTE.length],
    borderWidth: 1,
    borderRadius: 4,
  }));

  const chart = new Chart(canvas, {
    type: 'bar',
    data: {
      labels,
      datasets: chartDatasets,
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: options?.horizontal ? 'y' : 'x',
      scales: {
        x: {
          stacked: options?.stacked ?? false,
          grid: {
            color: CHART_COLORS.border,
          },
          ticks: {
            color: CHART_COLORS.textMuted,
            font: {
              size: 11,
            },
            callback: function(value) {
              if (options?.horizontal && options?.yAxisFormatter) {
                return options.yAxisFormatter(value as number);
              }
              const label = labels[value as number];
              // Truncate long labels
              return label && label.length > 15 ? label.substring(0, 12) + '...' : label;
            },
          },
          border: {
            color: CHART_COLORS.border,
          },
        },
        y: {
          stacked: options?.stacked ?? false,
          beginAtZero: true,
          grid: {
            color: CHART_COLORS.border,
          },
          ticks: {
            color: CHART_COLORS.textMuted,
            font: {
              size: 11,
            },
            callback: function(value) {
              if (!options?.horizontal && options?.yAxisFormatter) {
                return options.yAxisFormatter(value as number);
              }
              return value.toLocaleString();
            },
          },
          border: {
            color: CHART_COLORS.border,
          },
        },
      },
      plugins: {
        legend: {
          labels: {
            color: CHART_COLORS.textMuted,
            usePointStyle: true,
            padding: 16,
            font: {
              size: 12,
            },
          },
        },
        tooltip: {
          backgroundColor: 'rgba(32, 32, 32, 0.95)',
          titleColor: CHART_COLORS.text,
          bodyColor: CHART_COLORS.textMuted,
          borderColor: CHART_COLORS.border,
          borderWidth: 1,
          cornerRadius: 8,
          padding: 12,
          displayColors: true,
          boxPadding: 4,
          callbacks: {
            label: (context) => {
              const value = context.raw as number;
              const formatted = options?.yAxisFormatter
                ? options.yAxisFormatter(value)
                : value.toLocaleString();
              return `${context.dataset.label}: ${formatted}`;
            },
          },
        },
      },
    },
  });

  return {
    element: container,
    chart,
    destroy: () => chart.destroy(),
    update: () => chart.update(),
  };
}

/**
 * Creates a stacked bar chart useful for showing composition over time
 */
export function createStackedBarChart(
  labels: string[],
  datasets: Array<{
    label: string;
    data: number[];
    color?: string;
  }>,
  options?: {
    yAxisFormatter?: (value: number) => string;
  }
): ChartInstance {
  const chartOptions: {
    horizontal?: boolean;
    stacked?: boolean;
    yAxisFormatter?: (value: number) => string;
  } = {
    stacked: true,
  };
  if (options?.yAxisFormatter) {
    chartOptions.yAxisFormatter = options.yAxisFormatter;
  }
  return createBarChart(labels, datasets, chartOptions);
}

/**
 * Creates a horizontal bar chart
 */
export function createHorizontalBarChart(
  labels: string[],
  datasets: Array<{
    label: string;
    data: number[];
    color?: string;
  }>,
  options?: {
    yAxisFormatter?: (value: number) => string;
  }
): ChartInstance {
  const chartOptions: {
    horizontal?: boolean;
    stacked?: boolean;
    yAxisFormatter?: (value: number) => string;
  } = {
    horizontal: true,
  };
  if (options?.yAxisFormatter) {
    chartOptions.yAxisFormatter = options.yAxisFormatter;
  }
  return createBarChart(labels, datasets, chartOptions);
}

/**
 * Export chart colors for use in other components
 */
export { CHART_COLORS, PALETTE };
